import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';
import '../styles/fiscal.css';
import '../styles/fiscalModerno.css';

// Fiscal → Tarefas Acessórias: obrigações do departamento Fiscal cadastradas no Acessórias,
// só das atividades controladas, pelo mês do prazo. "Sincronizar" só LÊ o Acessórias e roda
// em blocos (a tela chama de novo até concluir). A lista de atividades controladas é da gestão.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const semAcento = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const dataBr = (iso) => (iso ? iso.split('-').reverse().join('/') : '—');
const compBr = (iso) => (iso ? `${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');
const dhBr = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)} ${s.slice(11, 16)}` : '');
const hojeMes = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

const SITUACAO = {
  atrasada: { rotulo: 'Atrasada', classe: 'erro', icone: 'fa-circle-exclamation', kpi: 'c-bad' },
  prazo_tecnico: { rotulo: 'Prazo técnico', classe: 'alerta', icone: 'fa-stopwatch', kpi: 'c-hot' },
  vence: { rotulo: 'Vence em até 3 dias', classe: 'alerta', icone: 'fa-hourglass-half', kpi: 'c-warn' },
  pendente: { rotulo: 'Pendente', classe: 'neutro', icone: 'fa-clock', kpi: 'c-navy' },
  entregue: { rotulo: 'Entregue', classe: 'ok', icone: 'fa-circle-check', kpi: 'c-ok' },
  entregue_atraso: { rotulo: 'Entregue com atraso', classe: 'ok', icone: 'fa-circle-check', kpi: 'c-ok' },
  dispensada: { rotulo: 'Dispensada', classe: 'neutro', icone: 'fa-ban', kpi: 'c-cyan' },
};
const EM_ABERTO = ['atrasada', 'prazo_tecnico', 'vence', 'pendente'];

function Selo({ s }) {
  const c = SITUACAO[s] || SITUACAO.pendente;
  return <span className={`fz-selo ${c.classe}`}><i className={`fas ${c.icone}`}></i>{c.rotulo}</span>;
}

export default function FiscalTarefasAcessorias() {
  const [mes, setMes] = useState(hojeMes());
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [busca, setBusca] = useState('');
  const [atividade, setAtividade] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [situacao, setSituacao] = useState('');
  const [soMinhas, setSoMinhas] = useState(false);
  const [soAbertas, setSoAbertas] = useState(true);
  const [soMulta, setSoMulta] = useState(false);
  const [visao, setVisao] = useState('atividade');   // 'atividade' (resumo) | 'obrigacao' (lista)
  const [sync, setSync] = useState(null);           // estado da sincronização em andamento
  const [sincronizando, setSincronizando] = useState(false);
  const [modalAtiv, setModalAtiv] = useState(null);  // lista de atividades (gestão)
  const [novaAtiv, setNovaAtiv] = useState('');
  const [salvandoAtiv, setSalvandoAtiv] = useState(false);
  const montado = useRef(true);
  useEffect(() => { montado.current = true; return () => { montado.current = false; }; }, []);

  const [ano, mesNum] = mes.split('-').map(Number);

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/api/fiscal/acessorias/tarefas/`, { params: { mes: mesNum, ano, _: Date.now() }, headers: headers() })
      .then((r) => { setDados(r.data); setErro(''); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível carregar as tarefas'))
      .finally(() => setCarregando(false));
  }, [mesNum, ano]);
  useEffect(() => { carregar(); }, [carregar]);

  // Sincronização em blocos: chama de novo até o backend dizer "concluido".
  const sincronizar = async () => {
    setSincronizando(true); setErro(''); setMsg('');
    let reiniciar = true;
    try {
      for (;;) {
        const r = await axios.post(`${API}/api/fiscal/acessorias/tarefas/sincronizar/`, { mes: mesNum, ano, reiniciar }, { headers: headers() });
        reiniciar = false;
        if (!montado.current) return;
        setSync(r.data.sync);
        if (r.data.sync.concluido) {
          const s = r.data.sync;
          setMsg(`Sincronizado: ${s.linhas} entrega(s) de ${s.total} empresa(s)${s.erros.length ? ` · ${s.erros.length} empresa(s) com erro` : ''}`);
          break;
        }
      }
      carregar();
    } catch (e) {
      if (montado.current) setErro(e.response?.data?.mensagem || 'Não foi possível sincronizar com o Acessórias');
    } finally {
      if (montado.current) setSincronizando(false);
    }
  };

  const itens = useMemo(() => dados?.itens || [], [dados]);
  const atividades = useMemo(() => [...new Set(itens.map((i) => i.atividade))].sort(), [itens]);
  const responsaveis = useMemo(() => [...new Set(itens.map((i) => i.responsavel).filter(Boolean))].sort(), [itens]);
  const meuEmail = dados?.meu_email || '';

  // filtros de empresa/atividade/responsável/"minhas" (sem a situação) — base dos indicadores
  const base = useMemo(() => {
    const termo = semAcento(busca.trim());
    const digitos = busca.replace(/\D/g, '');
    return itens.filter((i) => {
      if (atividade && i.atividade !== atividade) return false;
      if (soMulta && !i.multa) return false;
      if (responsavel && i.responsavel !== responsavel) return false;
      if (soMinhas && (!meuEmail || i.responsavel_email !== meuEmail)) return false;
      if (!termo) return true;
      return String(i.cod) === busca.trim() || semAcento(i.cliente).includes(termo)
        || (digitos.length >= 4 && (i.cnpj || '').includes(digitos));
    });
  }, [itens, busca, atividade, responsavel, soMinhas, meuEmail, soMulta]);

  const contagem = useMemo(() => {
    const c = { total: base.length };
    Object.keys(SITUACAO).forEach((k) => { c[k] = 0; });
    c.multaAtrasada = 0;
    base.forEach((i) => { c[i.situacao] = (c[i.situacao] || 0) + 1; if (i.multa && i.situacao === 'atrasada') c.multaAtrasada += 1; });
    return c;
  }, [base]);

  const lista = useMemo(() => base.filter((i) => {
    if (situacao) return situacao === 'entregue' ? i.situacao.startsWith('entregue') : i.situacao === situacao;
    return !soAbertas || EM_ABERTO.includes(i.situacao);
  }), [base, situacao, soAbertas]);

  const porAtividade = useMemo(() => {
    const m = {};
    base.forEach((i) => {
      const a = m[i.atividade] || (m[i.atividade] = { atividade: i.atividade, total: 0, entregues: 0, abertas: 0, atrasadas: 0, tecnico: 0, dispensadas: 0, multa: 0 });
      if (i.multa) a.multa += 1;
      a.total += 1;
      if (i.situacao.startsWith('entregue')) a.entregues += 1;
      else if (i.situacao === 'dispensada') a.dispensadas += 1;
      else { a.abertas += 1; if (i.situacao === 'atrasada') a.atrasadas += 1; if (i.situacao === 'prazo_tecnico') a.tecnico += 1; }
    });
    return Object.values(m).sort((x, y) => y.atrasadas - x.atrasadas || y.abertas - x.abertas || x.atividade.localeCompare(y.atividade));
  }, [base]);

  const filtrarSituacao = (s) => { setSituacao((atual) => (atual === s ? '' : s)); };

  // ---- atividades controladas (gestão) ----
  const abrirAtividades = () => {
    setModalAtiv({ lista: null });
    axios.get(`${API}/api/fiscal/acessorias/tarefas/atividades/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => setModalAtiv({ lista: r.data.atividades }))
      .catch((e) => { setErro(e.response?.data?.mensagem || 'Não foi possível carregar as atividades'); setModalAtiv(null); });
  };
  const gravarAtividade = (nome, controlar) => {
    setSalvandoAtiv(true);
    axios.post(`${API}/api/fiscal/acessorias/tarefas/atividades/`, { nome, controlar }, { headers: headers() })
      .then((r) => { setModalAtiv({ lista: r.data.atividades }); setNovaAtiv(''); })
      .catch((e) => setErro(e.response?.data?.mensagem || 'Não foi possível salvar'))
      .finally(() => setSalvandoAtiv(false));
  };
  const fecharAtividades = () => { setModalAtiv(null); carregar(); };

  const ultimo = sync || dados?.sync;
  const ultimoDoMes = ultimo && ultimo.mes === mesNum && ultimo.ano === ano;
  const pct = sync && sync.total ? Math.round((sync.indice / sync.total) * 100) : 0;
  const classePrazo = (i) => (i.situacao === 'atrasada' ? 'text-danger fw-bold' : ['vence', 'prazo_tecnico'].includes(i.situacao) ? 'fz-prazo-proximo' : '');

  const kpis = [
    { chave: 'atrasada', valor: contagem.atrasada },
    { chave: 'prazo_tecnico', valor: contagem.prazo_tecnico },
    { chave: 'vence', valor: contagem.vence },
    { chave: 'pendente', valor: contagem.pendente },
    { chave: 'entregue', valor: contagem.entregue + contagem.entregue_atraso, rotulo: 'Entregues' },
    { chave: 'dispensada', valor: contagem.dispensada },
  ];

  return (
    <div className="painel-op fz-app">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-clipboard-check header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">Tarefas Acessórias</h2>
          </div>
          <div className="gr-toolbar">
            <input type="month" className="fz-sel" value={mes} onChange={(e) => e.target.value && setMes(e.target.value)} title="Mês do prazo" disabled={sincronizando} />
            <button type="button" className="gr-btn" onClick={carregar} disabled={carregando || sincronizando}>
              <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
            </button>
            {dados?.pode_gerir && (
              <button type="button" className="gr-btn" onClick={abrirAtividades} disabled={sincronizando}>
                <i className="fas fa-sliders" aria-hidden="true"></i>Atividades
              </button>
            )}
            <button type="button" className="gr-btn primaria" onClick={sincronizar} disabled={sincronizando}>
              <i className={`fas ${sincronizando ? 'fa-spinner fa-spin' : 'fa-cloud-arrow-down'}`} aria-hidden="true"></i>
              {sincronizando ? `Sincronizando ${pct}%` : 'Sincronizar com o Acessórias'}
            </button>
          </div>
        </div>

        {sincronizando && sync && (
          <div className="mb-3">
            <div className="progress" style={{ height: 6 }}><div className="progress-bar" style={{ width: `${pct}%`, background: '#00b5cc' }}></div></div>
            <div className="fz-sub mt-1">{sync.indice} de {sync.total} empresas lidas · {sync.linhas} entrega(s) · só leitura no Acessórias</div>
          </div>
        )}
        {!sincronizando && (
          <div className="fz-sub mb-2">
            <i className="fas fa-circle-info me-1"></i>
            {ultimoDoMes && ultimo.concluido_em
              ? <>Última sincronização deste mês: {dhBr(ultimo.concluido_em)}{ultimo.erros?.length ? ` · ${ultimo.erros.length} empresa(s) com erro` : ''}.</>
              : ultimoDoMes && !ultimo.concluido
                ? <>Sincronização deste mês incompleta ({ultimo.indice}/{ultimo.total}). Clique em "Sincronizar" para concluir.</>
                : <>Este mês ainda não foi sincronizado — clique em "Sincronizar com o Acessórias".</>}
            {' '}Mostra as obrigações do Fiscal com prazo no mês, das atividades controladas.
          </div>
        )}
        {erro && <div className="alert alert-danger py-2" role="button" onClick={() => setErro('')}><i className="fas fa-circle-exclamation me-2"></i>{erro}</div>}
        {msg && <div className="alert alert-success py-2" role="button" onClick={() => setMsg('')}><i className="fas fa-circle-check me-2"></i>{msg}</div>}
        {!sincronizando && ultimoDoMes && ultimo.erros?.length > 0 && (
          <details className="small mb-2"><summary className="text-danger">Empresas que o Acessórias não devolveu</summary>
            <ul className="mb-0">{ultimo.erros.map((e) => <li key={e}>{e}</li>)}</ul>
          </details>
        )}

        <div className="po-kpis" style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}>
          {kpis.map((k) => {
            const c = SITUACAO[k.chave];
            return (
              <button type="button" key={k.chave} className={`po-kpi ${c.kpi} ${k.valor ? '' : 'po-zero'}`} onClick={() => filtrarSituacao(k.chave)}
                style={{ textAlign: 'left', outline: situacao === k.chave ? '2px solid #00b5cc' : 'none' }} title="Filtrar por esta situação">
                <div className="po-kpi-rotulo"><i className={`fas ${c.icone}`}></i>{k.rotulo || c.rotulo}</div>
                <div className="po-kpi-valor">{k.valor}</div>
                <div className="po-kpi-sub">{k.chave === 'atrasada' && contagem.multaAtrasada ? <span className="text-danger fw-semibold">{contagem.multaAtrasada} geram multa</span> : `de ${contagem.total}`}</div>
              </button>
            );
          })}
        </div>

        <div className="fz-filtros">
          <select className="fz-sel fw-semibold" value={visao} onChange={(e) => setVisao(e.target.value)} aria-label="Visão">
            <option value="atividade">Ver por atividade</option>
            <option value="obrigacao">Ver por obrigação</option>
          </select>
          <input className="po-busca" placeholder="Código, nome ou CNPJ da empresa" value={busca} onChange={(e) => setBusca(e.target.value)} />
          <select className="fz-sel" value={atividade} onChange={(e) => setAtividade(e.target.value)} aria-label="Atividade">
            <option value="">Todas as atividades</option>
            {atividades.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className="fz-sel" value={responsavel} onChange={(e) => setResponsavel(e.target.value)} aria-label="Responsável">
            <option value="">Todos os responsáveis</option>
            {responsaveis.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className="fz-sel" value={situacao} onChange={(e) => setSituacao(e.target.value)} aria-label="Situação">
            <option value="">{soAbertas ? 'Em aberto' : 'Todas as situações'}</option>
            <option value="atrasada">Atrasadas</option><option value="prazo_tecnico">No prazo técnico</option><option value="vence">Vencem em até 3 dias</option>
            <option value="pendente">Pendentes</option><option value="entregue">Entregues</option><option value="dispensada">Dispensadas</option>
          </select>
          <label className="po-toggle" title={meuEmail ? `Responsável pelo prazo = ${meuEmail}` : 'Seu login não tem e-mail de operador'}>
            <input type="checkbox" checked={soMinhas} onChange={(e) => setSoMinhas(e.target.checked)} disabled={!meuEmail} />Só as minhas
          </label>
          <label className="po-toggle" title="Só obrigações que geram multa por atraso"><input type="checkbox" checked={soMulta} onChange={(e) => setSoMulta(e.target.checked)} />Só as que geram multa</label>
          <label className="po-toggle"><input type="checkbox" checked={soAbertas} onChange={(e) => setSoAbertas(e.target.checked)} disabled={!!situacao} />Só em aberto</label>
        </div>

        {carregando && !dados ? (
          <div className="text-center py-5 text-muted"><i className="fas fa-spinner fa-spin fa-3x mb-3" style={{ color: '#00b5cc' }}></i><div className="fs-5">Carregando tarefas...</div></div>
        ) : (
          <>
            {visao === 'atividade' && (
              <section className="po-card po-enter">
                <header className="po-card-head">
                  <h3><i className="fas fa-layer-group" aria-hidden="true"></i>Por atividade</h3>
                  <span className="badge bg-light text-dark">{porAtividade.length}</span>
                </header>
                <div className="po-card-body">
                  <table className="table table-hover po-table align-middle mb-0">
                    <thead><tr><th>Atividade</th><th className="text-center">Multa</th><th className="text-end">Empresas</th><th className="text-end">Entregues</th><th className="text-end">Em aberto</th><th className="text-end">Atrasadas</th><th className="text-end">Prazo técnico</th><th className="text-end">Dispensadas</th><th style={{ width: 180 }}>Andamento</th></tr></thead>
                    <tbody>
                      {porAtividade.map((a) => {
                        const feitas = a.total ? Math.round(((a.entregues + a.dispensadas) / a.total) * 100) : 0;
                        return (
                          <tr key={a.atividade} role="button" onClick={() => { setAtividade(a.atividade); setVisao('obrigacao'); }}
                            title="Ver as obrigações desta atividade">
                            <td className="fw-semibold">{a.atividade}</td>
                            <td className="text-center">{a.multa ? <span className="fz-selo erro" title={`${a.multa} de ${a.total} empresa(s) com multa por atraso`}><i className="fas fa-triangle-exclamation"></i>{a.multa === a.total ? 'gera multa' : `${a.multa}/${a.total}`}</span> : <span className="text-muted small">não</span>}</td>
                            <td className="text-end fz-num">{a.total}</td>
                            <td className="text-end fz-num">{a.entregues}</td>
                            <td className="text-end fz-num">{a.abertas}</td>
                            <td className={`text-end fz-num ${a.atrasadas ? 'text-danger fw-bold' : ''}`}>{a.atrasadas}</td>
                            <td className={`text-end fz-num ${a.tecnico ? 'fw-bold' : ''}`} style={a.tecnico ? { color: '#e8590c' } : undefined}>{a.tecnico}</td>
                            <td className="text-end fz-num">{a.dispensadas}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div className="progress flex-grow-1" style={{ height: 6 }}><div className="progress-bar" style={{ width: `${feitas}%`, background: a.atrasadas ? '#c92a2a' : '#198754' }}></div></div>
                                <span className="fz-sub fz-num">{feitas}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {porAtividade.length === 0 && (
                        <tr><td colSpan={9} className="text-center text-muted py-4">
                          {itens.length ? 'Nenhuma atividade com esses filtros.' : 'Nada neste mês ainda. Clique em "Sincronizar com o Acessórias".'}
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {visao === 'obrigacao' && (
            <section className="po-card po-enter">
              <header className="po-card-head">
                <h3><i className="fas fa-clipboard-check" aria-hidden="true"></i>Obrigações{atividade ? ` · ${atividade}` : ''}</h3>
                <span className="badge bg-light text-dark">{lista.length} de {itens.length}</span>
                {atividade && <button type="button" className="btn btn-sm btn-link ms-auto" onClick={() => { setAtividade(''); setVisao('atividade'); }}><i className="fas fa-arrow-left me-1"></i>Voltar às atividades</button>}
              </header>
              <div className="po-card-body">
                <table className="table table-hover po-table align-middle mb-0">
                  <thead><tr><th>Empresa</th><th>Atividade</th><th className="text-center">Multa</th><th>Competência</th><th>Prazo</th><th>Situação</th><th>Responsável</th><th>Entregue em</th></tr></thead>
                  <tbody>
                    {lista.map((i) => (
                      <tr key={i.id}>
                        <td><div className="fw-semibold">{i.cliente}</div><div className="fz-sub fz-num">{i.cod}</div></td>
                        <td>{i.atividade}</td>
                        <td className="text-center">{i.multa ? <span className="fz-selo erro" title="Gera multa se atrasar"><i className="fas fa-triangle-exclamation"></i>sim</span> : <span className="text-muted small">não</span>}</td>
                        <td className="fz-num">{compBr(i.competencia)}</td>
                        <td className={`fz-num ${classePrazo(i)}`}>{dataBr(i.prazo)}{i.data_atraso && <div className="fz-sub">atraso a partir de {dataBr(i.data_atraso)}</div>}</td>
                        <td><Selo s={i.situacao} />{i.status && <div className="fz-sub mt-1">{i.status}</div>}</td>
                        <td className="small">{i.responsavel || <span className="text-muted">—</span>}</td>
                        <td className="small fz-num">{i.entregue_em ? dataBr(i.entregue_em) : '—'}{i.entregue_por && <div className="fz-sub">{i.entregue_por}</div>}</td>
                      </tr>
                    ))}
                    {lista.length === 0 && (
                      <tr><td colSpan={8} className="text-center text-muted py-4">
                        {itens.length ? 'Nenhuma obrigação com esses filtros.' : 'Nada neste mês ainda. Clique em "Sincronizar com o Acessórias".'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
            )}
          </>
        )}

        {modalAtiv && (
          <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}><i className="fas fa-sliders me-2" style={{ color: '#00b5cc' }}></i>Atividades controladas</h5>
                    <small style={{ color: '#6c757d' }}>Só as marcadas aparecem na tela e entram na sincronização</small>
                  </div>
                  <button type="button" className="btn-close" onClick={fecharAtividades}></button>
                </div>
                <div className="modal-body">
                  {!modalAtiv.lista ? (
                    <div className="text-muted"><i className="fas fa-spinner fa-spin me-2"></i>Carregando...</div>
                  ) : (
                    <>
                      <div className="input-group mb-3">
                        <input className="form-control" placeholder="Incluir atividade (nome exatamente como no Acessórias)" value={novaAtiv} onChange={(e) => setNovaAtiv(e.target.value)} maxLength={200} />
                        <button className="btn btn-primary" onClick={() => gravarAtividade(novaAtiv.trim(), true)} disabled={salvandoAtiv || !novaAtiv.trim()}>
                          <i className="fas fa-plus me-1"></i>Incluir
                        </button>
                      </div>
                      <div className="fz-sub mb-2">{modalAtiv.lista.filter((a) => a.controlar).length} de {modalAtiv.lista.length} controladas</div>
                      <div className="row g-1">
                        {modalAtiv.lista.map((a) => (
                          <div className="col-md-6" key={a.nome}>
                            <label className="form-check d-flex align-items-center gap-2 mb-0 py-1">
                              <input type="checkbox" className="form-check-input m-0" checked={a.controlar} disabled={salvandoAtiv}
                                onChange={(e) => gravarAtividade(a.nome, e.target.checked)} />
                              <span className={a.controlar ? '' : 'text-muted'}>{a.nome}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer bg-light">
                  <div className="fz-sub me-auto">Atividade nova entra na tela depois da próxima sincronização.</div>
                  <button className="btn btn-secondary" onClick={fecharAtividades}><i className="fas fa-check me-2"></i>Fechar</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
